import { useState } from "react";
import { MapPin, Phone, Mail, Send, Clock } from "lucide-react";
import { SiZalo } from "react-icons/si";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { OFFICES, CONTACT } from "@/data/services";

const ZALO_LINK = "https://zalo.me/84832240483";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const submit = (e) => {
    e.preventDefault();
    toast.success("Message ready", { description: "Please reach us by email, phone or Zalo — we respond quickly." });
    setForm({ name: "", email: "", message: "" });
  };

  return (
    <div>
      <section className="bg-brand-navy text-white grain">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-gold font-bold mb-3">Get in touch</p>
            <h1 className="font-serif text-5xl md:text-6xl tracking-tight">We're here to help</h1>
            <p className="mt-5 text-slate-300 text-lg max-w-2xl">Two offices in Vietnam and multiple ways to reach our immigration consultants.</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12">
        <div className="space-y-6">
          {OFFICES.map((o) => (
            <Reveal key={o.city}>
              <div className="rounded-3xl border border-slate-200 bg-white p-7 hover:shadow-soft transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-brand-red/10 flex items-center justify-center text-brand-red"><MapPin className="w-5 h-5" /></div>
                  <h3 className="font-serif text-2xl text-brand-navy">{o.city} Office</h3>
                </div>
                <p className="text-slate-600">{o.address}</p>
                <p className="mt-2 text-sm text-slate-500 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Mon–Fri, 8:30–17:30 (ICT)</p>
              </div>
            </Reveal>
          ))}

          <Reveal>
            <div className="grid sm:grid-cols-3 gap-4">
              <a href={`tel:${CONTACT.phone}`} data-testid="contact-phone" className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-brand-red transition-colors">
                <Phone className="w-5 h-5 text-brand-red mb-2" />
                <p className="text-xs text-slate-500">Call us</p>
                <p className="text-sm font-semibold text-brand-navy">{CONTACT.phoneDisplay}</p>
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-brand-red transition-colors">
                <Mail className="w-5 h-5 text-brand-red mb-2" />
                <p className="text-xs text-slate-500">Email us</p>
                <p className="text-sm font-semibold text-brand-navy break-all">{CONTACT.email}</p>
              </a>
              <a href={ZALO_LINK} target="_blank" rel="noopener noreferrer" data-testid="contact-zalo" className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#0068FF] transition-colors">
                <SiZalo className="w-5 h-5 text-[#0068FF] mb-2" />
                <p className="text-xs text-slate-500">Chat on</p>
                <p className="text-sm font-semibold text-brand-navy">Zalo</p>
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal>
          <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-8 shadow-soft" data-testid="contact-form">
            <h2 className="font-serif text-3xl text-brand-navy tracking-tight">Send us a message</h2>
            <p className="text-sm text-slate-500 mt-2 mb-6">For applications, please register and use your dashboard so we can track everything securely.</p>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input"
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-brand-red transition-colors" placeholder="Your name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input"
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-brand-red transition-colors" placeholder="you@email.com" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">Message</label>
                <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input"
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-brand-red transition-colors resize-none" placeholder="How can we help?" />
              </div>
              <button type="submit" data-testid="contact-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors shadow-redglow">
                <Send className="w-4 h-4" /> Send message
              </button>
            </div>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
